import type React from 'react';
import { forwardRef, useEffect, useState } from 'react';

import { type ApolloError, useQuery } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';
import Search from '@crehana/icons/outline/Search';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, Loader, TextField } from '@crehana/ui';
import { notEmpty } from '@crehana/utils';
import { useUITheme } from '@crehana/web';
import * as Sentry from '@sentry/nextjs';
import { BehaviorSubject, from } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

import localeEs from '../../i18n/localeEs';
import GET_ORG_USERS_QUERY from './graphql/GetOrgUsersQuery.b2b.graphql';
import type {
  GetOrgUsersQuery,
  GetOrgUsersQueryVariables,
} from './graphql/types/GetOrgUsersQuery';
import type { SearchInputProps } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// biome-ignore lint/complexity/noBannedTypes: legacy type usage
const _usersBehavior = new BehaviorSubject<any | {}>({});

const SearchInput = (
  { slug, onIds }: SearchInputProps,
  ref: React.Ref<HTMLInputElement>,
) => {
  const { baseLighter } = colors;
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const [searchKey, setSearchKey] = useState('');
  const { loading, refetch } = useQuery<
    GetOrgUsersQuery,
    GetOrgUsersQueryVariables
  >(GET_ORG_USERS_QUERY, {
    fetchPolicy: 'no-cache',
    skip: !searchKey || !slug,
    context: {
      clientName: 'b2b',
    },
    notifyOnNetworkStatusChange: true,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    const subject = _usersBehavior
      .pipe(
        debounceTime(500),
        switchMap(() =>
          from(
            refetch({
              slug,
              searchKey: searchKey || '',
            })
              .then(({ data }) => {
                let ids: string[] = [];
                const users =
                  data.userOrganizationSearch?.edges.filter(notEmpty) || [];

                if (users.length > 0 && searchKey) {
                  ids =
                    users.reduce((acc = [], curr) => {
                      if (curr?.node?.originalId) {
                        acc.push(`${curr?.node?.originalId}`);
                      }
                      return acc;
                    }, [] as string[]) || [];
                } else if (searchKey) {
                  ids = ['0'];
                }

                onIds(ids);
              })
              .catch((error: ApolloError) => {
                Sentry.captureException(error);
              }),
          ),
        ),
      )
      .subscribe();

    return () => {
      subject.unsubscribe();
    };
  }, [searchKey]);

  return (
    <TextField
      ref={ref}
      hint={t('SEARCH_BY_USERS_HINT', {
        defaultValue: localeEs.es.SEARCH_BY_USERS_HINT,
      })}
      chat
      darkMode={isDark}
      onChange={e => {
        if (e.target.value) {
          setSearchKey(e.target.value);
        } else {
          setSearchKey('');
          onIds([]);
        }
      }}
      startAdornment={
        loading ? (
          <Loader size={24} />
        ) : (
          <Search color={baseLighter} size={24} />
        )
      }
    />
  );
};

export default forwardRef(SearchInput);
